import { CommonModule } from '@angular/common';
import { Component, OnInit, Optional, DestroyRef, inject } from '@angular/core';
import { MatCheckboxModule } from '@angular/material/checkbox';
import { MatMenuModule } from '@angular/material/menu';
import { MatRadioModule } from '@angular/material/radio';
import { FormGroup, FormBuilder } from '@angular/forms';
import { takeUntilDestroyed } from '@angular/core/rxjs-interop';

import { IntegracaoConstants } from 'src/app/constants/integracao';
import { ModulosDeFormulario } from 'src/app/utils/formsModules';
import { DateRangePickerComponent } from 'src/app/components/date-range-picker/date-range-picker.component';
import { FiltroCotacoesRamoService } from '../../filtros/cotacoes-ramo/filtro-cotacoes-ramo.service';
import { FiltroComponent } from '../../filtro.component';

type PeriodoFiltroValue = number | 'renovacao';

interface PeriodoOption {
  value: PeriodoFiltroValue;
  viewValue: string;
}

@Component({
  selector: 'sub-filtro-periodo-renovacao',
  templateUrl: './periodo-renovacao.component.html',
  styleUrls: ['../../filtro.component.scss'],
  imports: [
    CommonModule,
    ModulosDeFormulario,
    MatCheckboxModule,
    MatRadioModule,
    MatMenuModule,
    DateRangePickerComponent,
  ],
})
export class PeriodoRenovacaoComponent implements OnInit {
  protected readonly periodoOptions: PeriodoOption[] = IntegracaoConstants.periodoOptions
    .filter((periodo) => typeof periodo.value === 'number' || periodo.value === 'renovacao')
    .map((periodo) => ({ value: periodo.value as PeriodoFiltroValue, viewValue: periodo.viewValue }));
  protected selectedPeriodo: PeriodoFiltroValue | null = 15;
  protected dateRangeForm: FormGroup;

  private destroyRef = inject(DestroyRef);

  constructor(
    private filtroCotacoesRamo: FiltroCotacoesRamoService,
    private fb: FormBuilder,
    @Optional() private filtroComp?: FiltroComponent
  ) {
    this.dateRangeForm = this.fb.group({
      startDate: [null],
      endDate: [null],
    });
  }

  ngOnInit(): void {
    this.initializeDefaultDates();
    this.subscribeToChanges();
  }

  private initializeDefaultDates(): void {
    const endDate = new Date();
    const startDate = new Date();
    startDate.setDate(startDate.getDate() - 15);

    this.dateRangeForm.patchValue({
      startDate,
      endDate,
    });
  }

  private subscribeToChanges(): void {
    this.filtroCotacoesRamo.periodo$
      .pipe(takeUntilDestroyed(this.destroyRef))
      .subscribe((periodo) => {
        this.selectedPeriodo = periodo;

        if (typeof periodo === 'number' && periodo > 0) {
          this.updateDateRangeFromPeriodo(periodo);
        }
      });

    this.dateRangeForm.valueChanges
      .pipe(takeUntilDestroyed(this.destroyRef))
      .subscribe(() => {
        this.selectedPeriodo = null;
        this.emitFilter();
      });
  }

  protected onFiltroChange(newValue: PeriodoFiltroValue): void {
    this.selectedPeriodo = newValue;

    if (typeof newValue === 'number' && newValue > 0) {
      this.updateDateRangeFromPeriodo(newValue);
    }

    this.filtroCotacoesRamo.changePeriodo(newValue);
    this.filtroComp?.sendFilter(false);
  }

  protected onRadioClick(event: MouseEvent, value: PeriodoFiltroValue): void {
    if (this.selectedPeriodo === value) {
      event.preventDefault();
      this.selectedPeriodo = 15;
      this.updateDateRangeFromPeriodo(15);
      this.filtroCotacoesRamo.changePeriodo(15);
      this.filtroComp?.sendFilter(false);
    }
  }

  private updateDateRangeFromPeriodo(dias: number): void {
    const endDate = new Date();
    const startDate = new Date();
    startDate.setDate(startDate.getDate() - dias);

    this.dateRangeForm.patchValue(
      {
        startDate,
        endDate,
      },
      { emitEvent: false }
    );
  }

  private emitFilter(): void {
    const { startDate, endDate } = this.dateRangeForm.value;
    if (startDate && endDate) {
      this.filtroCotacoesRamo.changePeriodoCustom(startDate, endDate);
    }

    this.filtroComp?.sendFilter(false);
  }
}